// src/components/materials/MaterialStatusBadge.tsx
//
// One badge, two facts: where generation got to, and where review got to.
//
// They are SEPARATE in PitchLess and are kept separate here. A material can be
// `ready` and `needs-revision` at the same time - the document generated fine,
// a reviewer asked for changes - and collapsing that into a single word is how
// a user ends up thinking a rejected proposal is finished.
//
// UNKNOWN STATES. PitchLess owns this vocabulary and may add to it. Anything
// not listed reads as "Working" with a spinner, never as done: showing a
// spinner for something finished is a small annoyance; showing "Ready" for
// something still generating hands the user an empty document.

import React from 'react';
import { isBusy, type MaterialStatus, type MaterialReviewStatus } from '../../api/materialsApi';

const STATUS_LABEL: Partial<Record<MaterialStatus, string>> = {
  draft: 'Draft',
  queued: 'Queued',
  gathering_context: 'Gathering context',
  generating: 'Writing',
  rendering: 'Rendering',
  ready: 'Ready',
  in_review: 'In review',
  approved: 'Approved',
  exported: 'Exported',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const STATUS_CLASS: Partial<Record<MaterialStatus, string>> = {
  ready: 'bg-green-100 text-green-800',
  approved: 'bg-green-100 text-green-800',
  exported: 'bg-gray-100 text-gray-700',
  in_review: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-600',
};

const REVIEW_LABEL: Record<MaterialReviewStatus, string> = {
  drafting: 'Not submitted',
  'in-review': 'In review',
  approved: 'Approved',
  'needs-revision': 'Changes requested',
};

const REVIEW_CLASS: Record<MaterialReviewStatus, string> = {
  drafting: 'bg-gray-100 text-gray-700',
  'in-review': 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  'needs-revision': 'bg-orange-100 text-orange-800',
};

export interface MaterialStatusBadgeProps {
  status: MaterialStatus;
  reviewStatus?: MaterialReviewStatus;
}

export function MaterialStatusBadge({
  status,
  reviewStatus,
}: MaterialStatusBadgeProps): React.ReactElement {
  const busy = isBusy(status);
  const label = STATUS_LABEL[status] ?? 'Working';
  const cls = STATUS_CLASS[status] ?? 'bg-blue-100 text-blue-800';

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${cls}`}
        // Generation is asynchronous and can take a while. A screen reader
        // user needs to be told when it lands, not left on a stale value.
        aria-live={busy ? 'polite' : undefined}
      >
        {busy && (
          <span
            className="inline-block w-2 h-2 rounded-full bg-current opacity-60 animate-pulse"
            aria-hidden="true"
          />
        )}
        {label}
      </span>

      {/* Only worth showing once it says something the status does not. */}
      {reviewStatus && reviewStatus !== 'drafting' && status !== 'failed' && (
        <span
          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${REVIEW_CLASS[reviewStatus]}`}
        >
          {REVIEW_LABEL[reviewStatus]}
        </span>
      )}
    </span>
  );
}

export { REVIEW_LABEL, STATUS_LABEL };
